import { useMutation } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { CREATE_BROADCAST_OVERRIDE } from '@optools/services/operator-gateway/btc/mutations';
import { GET_BROADCAST_ACQUISITIONS } from '@optools/services/operator-gateway/btc/queries';
import { FeedTypeCode } from '@optools/services/operator-gateway/btc/types';
import { Region } from '@optools/services/operator-gateway/types/generated/graphql';
import { PlusCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { BroadcastOverrideForm, BroadcastOverrideFormValues } from './broadcast-override-form';

export interface CreateBroadcastOverrideDialogProps {
  callSign: string;
  feedTypeCode: FeedTypeCode;
  gamePk: number;
  region: Region;
}

export function CreateBroadcastOverrideDialog({
  callSign,
  feedTypeCode,
  gamePk,
  region,
}: CreateBroadcastOverrideDialogProps) {
  const [open, setOpen] = useState(false);

  const [createOverride, { loading }] = useMutation(CREATE_BROADCAST_OVERRIDE, {
    onCompleted: ({ createBroadcastOverride }) => {
      const title = 'Create Broadcast Override';

      if (!createBroadcastOverride) {
        toast.error(title, {
          description: 'Failed to create Broadcast Override',
          duration: Infinity,
        });
        return;
      }

      setOpen(false);
      toast.success(title, {
        description: `Created a new Broadcast Override ID: ${createBroadcastOverride.id}`,
      });
    },
    refetchQueries: [
      {
        query: GET_BROADCAST_ACQUISITIONS,
        variables: { feedTypeCode, gamePk },
      },
    ],
  });

  // Handle creating the broadcast override, if we are creating an override for the central
  // region ignore any incoming input label and use null until the central region supports an
  // input label
  const handleCreateBroadcastOverride = (formValues: BroadcastOverrideFormValues) => {
    const { fronthaulDecoderInputLabel, multicastAddress } = formValues;
    createOverride({
      variables: {
        callSign,
        feedTypeCode,
        fronthaulDecoderInputLabel: region === Region.EAST ? fronthaulDecoderInputLabel : null,
        gamePk,
        multicastAddress,
        region,
      },
    });
  };

  const handleFormReset = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => !loading && setOpen(newOpen)}>
      <DialogTrigger asChild>
        <Button aria-label="Create Broadcast Override" size="icon-xs">
          <PlusCircle />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Broadcast Override</DialogTitle>
          <DialogDescription className="sr-only">Create a new broadcast override</DialogDescription>
        </DialogHeader>
        <BroadcastOverrideForm
          callSign={callSign}
          disabled={loading}
          feedTypeCode={feedTypeCode}
          gamePk={gamePk}
          region={region}
          onCancel={handleFormReset}
          onSubmit={handleCreateBroadcastOverride}
        />
      </DialogContent>
    </Dialog>
  );
}
